/**
 * OnboardingProvider
 *
 * Single mount point for the entire onboarding guide UI.
 * Rendered via React portal so it sits above all page content without
 * affecting the DOM structure of any existing feature.
 *
 * Responsibilities:
 *  - Auto-triggers the guide once per major app version (via useOnboarding).
 *  - Measures the target element's bounding rect on each step change.
 *  - Re-measures on scroll / resize / route change so the spotlight stays aligned.
 *  - Computes isOnCorrectPage from the store's currentPathname (synced by
 *    OnboardingRouteSync which lives inside the router tree).
 *  - Handles Escape key to skip.
 *  - Scrolls the target element into view when the step changes.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { matchesScope, ROUTE_SCOPE_LABELS, CURRENT_GUIDE } from '../config';
import { useOnboarding } from '../hooks/useOnboarding';
import { useOnboardingStore } from '../store/useOnboardingStore';

import { OnboardingTooltip } from './OnboardingTooltip';
import type { SpotlightRect } from './SpotlightOverlay';
import { SpotlightOverlay } from './SpotlightOverlay';

export const OnboardingProvider = () => {
  const { skip, complete } = useOnboarding(true);
  const { isOpen, currentStep, next, prev, pause, currentPathname } =
    useOnboardingStore();

  const [rect, setRect] = useState<SpotlightRect | null>(null);
  const rafRef = useRef<number>();

  const step = CURRENT_GUIDE[currentStep];

  // ── Page-scope awareness ──────────────────────────────────────────────────
  const isOnCorrectPage =
    !step?.routeScope || matchesScope(currentPathname, step.routeScope);

  // Label of the page the user needs to navigate to (for the nav prompt).
  const requiredPageLabel = step?.routeScope
    ? ROUTE_SCOPE_LABELS[step.routeScope]
    : undefined;

  // ── Measure target element ────────────────────────────────────────────────
  const measure = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      if (!step?.target || !isOnCorrectPage) {
        setRect(null);
        return;
      }
      const el = document.querySelector(
        `[data-onboarding-id="${step.target}"]`,
      );
      if (!el) {
        setRect(null);
        return;
      }
      const br = el.getBoundingClientRect();
      const pad = step.padding ?? 8;
      setRect({
        x: br.left - pad,
        y: br.top - pad,
        width: br.width + pad * 2,
        height: br.height + pad * 2,
      });
    });
  }, [step, isOnCorrectPage]);

  // Clear stale rect immediately on step or route change so the spotlight
  // doesn't animate through an ugly intermediate shape to the next position.
  useEffect(() => {
    setRect(null);
  }, [currentStep, currentPathname]);

  // Re-measure on step change, guide open, route change, scroll, resize.
  useEffect(() => {
    if (!isOpen) return;

    if (isOnCorrectPage && step?.target) {
      const el = document.querySelector(
        `[data-onboarding-id="${step.target}"]`,
      );
      if (el) {
        el.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }
    }

    const t = setTimeout(measure, 80);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);

    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // currentPathname in deps so re-measure fires after every route change.
  }, [isOpen, measure, step, isOnCorrectPage, currentPathname]);

  // ── Escape key ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') pause();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, pause]);

  // !!!! prevent openning onboarding guide by this two flag !!!
  if (!isOpen || !step) return null;

  const isModal = step.type === 'modal';
  // Show spotlight only when on the correct page and not a modal step.
  const showSpotlight = isOnCorrectPage && !isModal;

  return createPortal(
    <>
      <SpotlightOverlay
        rect={showSpotlight ? rect : null}
        isModal={!showSpotlight}
      />
      <OnboardingTooltip
        step={step}
        currentStep={currentStep}
        totalSteps={CURRENT_GUIDE.length}
        rect={showSpotlight ? rect : null}
        isOnCorrectPage={isOnCorrectPage}
        requiredPageLabel={requiredPageLabel}
        onNext={next}
        onPrev={prev}
        onPause={pause}
        onSkip={skip}
        onComplete={complete}
      />
    </>,
    document.body,
  );
};
